import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Link as CopyLinkIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import { Business } from '@/constants/types';

interface ShareBusinessModalProps {
  open: boolean;
  onClose: () => void;
  business: Business | null;
}

export const ShareBusinessModal: React.FC<ShareBusinessModalProps> = ({
  open,
  onClose,
  business,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  if (!business) return null;

  const shareUrl = `https://hrtalky.in/businesses/${business._id}`;
  const shareTitle = business.businessName;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarOpen(true);
      onClose();
    });
  };

  const socialIcons = [
    { icon: <CopyLinkIcon />, label: 'Copy link', onClick: handleCopyLink },
    { component: FacebookShareButton, icon: <FacebookIcon />, label: 'Facebook', props: { hashtag: '#HRTalky' } },
    { component: TwitterShareButton, icon: <TwitterIcon />, label: 'X', props: { title: shareTitle, hashtags: ['HRTalky', 'Business'] } },
    { component: LinkedinShareButton, icon: <LinkedInIcon />, label: 'LinkedIn', props: { title: shareTitle, source: 'HRTalky' } },
    { component: WhatsappShareButton, icon: <WhatsAppIcon />, label: 'WhatsApp', props: { title: `${shareTitle}\n\n${shareUrl}`, separator: '' } },
  ];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 340, bgcolor: 'background.paper',
          boxShadow: 24, p: 3, borderRadius: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Share Business</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box>
            {socialIcons.map((social, i) => {
              const rowSx = {
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1, borderRadius: 2, mb: 0.5,
                cursor: 'pointer', width: '100%',
                '&:hover': { bgcolor: 'action.hover' },
              };
              if (social.onClick) {
                return (
                  <Box key={i} onClick={social.onClick} sx={rowSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </Box>
                );
              }
              const ShareBtn = social.component!;
              return (
                <Box key={i} sx={{ ...rowSx, p: 0 }}>
                  <ShareBtn url={shareUrl} {...(social.props || {})} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', width: '100%', color: 'inherit', textDecoration: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </ShareBtn>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>

      {/* Copy snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <SnackbarContent
          sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 2 }}
          message={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckIcon /><Typography variant="body2" sx={{ color: 'white' }}>Link copied!</Typography></Box>}
          action={<IconButton onClick={() => setSnackbarOpen(false)} color="inherit" size="small"><CloseIcon fontSize="small" /></IconButton>}
        />
      </Snackbar>
    </>
  );
};
